<template>
    <v-dialog v-model="show" max-width="1200px" scrollable>
        <v-card class="pa-4">
            <v-card-title>
                Atendimentos de &nbsp;<span class="primary-blue--text font-italic font-weight-bold">{{ customerData?.name }}</span>
            </v-card-title>
            <v-card-text>
                <v-data-table
                    :headers="headers"
                    :items="customerData?.sessions"
                    sort-by="session_date"
                    sort-desc
                >
                    <template v-slot:[`item.session_date`]="{ item }">
                        {{ formatDate(item.session_date, true) }}
                    </template>
                    <template v-slot:[`item.session_duration`]="{ item }">
                        {{ item.session_duration }} minutos
                    </template>
                    <template v-slot:[`item.attended`]="{ item }">
                        <span v-if="item.rescheduled">Reagendou <v-icon color="primary-blue">mdi-calendar-refresh-outline</v-icon></span>
                        <v-icon v-else-if="item.attended" color="secondary-green">mdi-check-bold</v-icon>
                        <v-icon v-else color="secondary-red">mdi-close-thick</v-icon>
                    </template>
                    <template v-slot:[`item.session_record`]="{ item }">
                        <span v-if="item.rescheduled">Reagendou <v-icon color="primary-blue">mdi-calendar-refresh-outline</v-icon></span>
                        <v-icon v-else-if="item.session_record" color="secondary-green">mdi-check-bold</v-icon>
                        <v-icon v-else color="secondary-red">mdi-close-thick</v-icon>
                    </template>
                    <template v-slot:[`item.session_value`]="{ item }">
                        {{ (item.session_value / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-row no-gutters class="flex-nowrap">
                            <v-col cols="auto" class="px-1">
                                <v-tooltip bottom v-if="isStarter">
                                    <template v-slot:activator="{ on, attrs }">
                                        <div v-bind="attrs" v-on="on" class="d-inline-block">
                                            <v-btn icon disabled color="primary">
                                                <v-icon>mdi-clipboard-text-outline</v-icon>
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>Prontuário (Premium)</span>
                                </v-tooltip>
                                <v-btn v-else icon @click="openMedicalRecord(item)" color="primary" title="Prontuário" :id="item.id === 'tour-fake-session-2' ? 'tour-list-sessions-medical-record' : null">
                                    <v-icon>
                                        mdi-clipboard-text-outline
                                    </v-icon>
                                </v-btn>
                            </v-col>
                            <v-col cols="auto" class="px-1">
                                <v-btn icon @click="openEditSession(item)" color="primary-blue">
                                    <v-icon>
                                        mdi-eye-outline
                                    </v-icon>
                                </v-btn>
                            </v-col>
                            <v-col cols="auto" class="px-1">
                                <v-btn icon @click="openDeleteSession(item)" color="tertiary-red">
                                    <v-icon>
                                        mdi-delete-empty-outline
                                    </v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>
                    </template>
                </v-data-table>
            </v-card-text>
            <v-card-actions>
                <v-row no-gutters justify="space-between">
                    <v-col cols="auto">
                        <v-btn dark color="primary-pink" outlined elevation="0" @click="close"><v-icon left>mdi-arrow-u-left-top</v-icon> Voltar</v-btn>
                    </v-col>
                    <v-col cols="auto" v-if="!isStarter">
                        <v-btn color="primary" elevation="0" @click="openExportModal" id="tour-list-sessions-export">
                            <v-icon left>mdi-file-pdf-box</v-icon> Exportar Prontuários
                        </v-btn>
                    </v-col>
                    <v-col cols="auto" v-else>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="on" class="d-inline-block">
                                    <v-btn disabled color="primary" outlined elevation="0" id="tour-list-sessions-export">
                                        <v-icon left>mdi-file-pdf-box</v-icon> Exportar Prontuários
                                        <v-icon right small>mdi-lock</v-icon>
                                    </v-btn>
                                </span>
                            </template>
                            <span>Recurso Premium</span>
                        </v-tooltip>
                    </v-col>
                </v-row>
            </v-card-actions>
            <dialog-edit-session :customer-data="show_edit_session ? customerData : null" :session-data="session_data" v-model="show_edit_session" @close="closeEditSession" @edited="$emit('updateData')" />
            <dialog-delete-session :customer-data="show_delete_session ? customerData : null" :session-data="session_data" v-model="show_delete_session" @close="closeDeleteSession" @deleted="$emit('updateData')" />
            <dialog-export-medical-records v-model="showDialogExport" ref="exportModal" />
            <dialog-medical-record v-if="session_data" v-model="show_medical_record" :session-data="session_data" @saved="$emit('updateData')" />
        </v-card>
    </v-dialog>
</template>

<script>
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

import DialogEditSession from "./DialogEditSession.vue";
import DialogDeleteSession from "./DialogDeleteSession.vue";
import DialogExportMedicalRecords from "./DialogExportMedicalRecords.vue";
import DialogMedicalRecord from "./DialogMedicalRecord.vue";

export default {
    components: {
        DialogEditSession,
        DialogDeleteSession,
        DialogExportMedicalRecords,
        DialogMedicalRecord
    },
    props: {
        value: {
            type: Boolean,
            required: false,
            default: false
        },
        customerData: {
            type: Object,
            required: false,
            default: null
        }
    },
    data() {
        return {
            show: this.value,
            headers: [
                { text: "Data", value: "session_date", sortable: true },
                { text: "Tipo", value: "service.name", sortable: true },
                { text: "Duração", value: "session_duration", sortable: true },
                { text: "Compareceu", value: "attended", sortable: true },
                { text: "Prontuário", value: "session_record", sortable: true },
                { text: "Valor", value: "session_value", sortable: true },
                { text: "Comentários", value: "comments", sortable: true },
                { text: '', value: 'actions', sortable: false },
            ],
            session_data: null,
            show_edit_session: false,
            show_delete_session: false,
            showDialogExport: false,
            show_medical_record: false
        };
    },
    computed: {
        isStarter() {
            return this.$store.state.auth.user?.access_profile_slug === 'starter';
        }
    },
    mounted() {
        window.$dialogListSessions = this;
    },
    beforeDestroy() {
        if (window.$dialogListSessions === this) {
            window.$dialogListSessions = null;
        }
    },
    watch: {
        value() {
            this.show = this.value;
        },
        show() {
            if (!this.show) {
                this.$emit("close");
            }

            this.$emit("input", this.show);
        }
    },
    methods: {
        formatDate(date, hour = false) {
            return date ? dayjs(date).format("DD/MM/YYYY" + (hour ? " HH:mm" : "")) : null;
        },
        close() {
            this.show = false;
        },
        closeEditSession() {
            this.show_edit_session = false;
            this.session_data = null;
        },
        closeDeleteSession() {
            this.show_delete_session = false;
            this.session_data = null;
        },
        openEditSession(session) {
            this.session_data = session;
            this.show_edit_session = true;
        },
        openDeleteSession(session) {
            this.session_data = session;
            this.show_delete_session = true;
        },
        openMedicalRecord(session) {
            this.session_data = session;
            this.show_medical_record = true;
        },
        openExportModal() {
            this.showDialogExport = true;
            this.$nextTick(() => {
                if (this.$refs.exportModal && this.customerData) {
                    this.$refs.exportModal.selectedCustomer = this.customerData;
                    this.$refs.exportModal.loadSessions();
                }
            });
        }
    }
};
</script>
